import React, { useState, useEffect } from "react";

// -----------------------------------------------------------------------------
// MoneyInput — INR-friendly numeric input.
//
// Fixes two pain points on the doc/PO line-items table:
//   1. A default "0" no longer sits in the field waiting to be back-spaced —
//      the input is empty when the underlying value is 0 or "".
//   2. On BLUR we format the number in Indian rupee notation (₹31,45,000.00);
//      on FOCUS we drop back to raw digits so the user can edit smoothly.
//
// Props (mirrors <Input>):
//   value        number | string
//   onChange     (event-like: { target: { value: rawNumericString } })
//   min, max     optional bounds
//   decimals     digits after decimal for the display (default 2)
//   className    passthrough for styling
// -----------------------------------------------------------------------------
const IN_FMT = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const IN_FMT_INT = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 0, maximumFractionDigits: 0,
});

function toRaw(v) {
  if (v === "" || v === null || v === undefined) return "";
  const n = typeof v === "number" ? v : parseFloat(String(v));
  if (!isFinite(n) || n === 0) return "";
  return String(v);
}

function toDisplay(v, decimals) {
  if (v === "" || v === null || v === undefined) return "";
  const n = typeof v === "number" ? v : parseFloat(String(v));
  if (!isFinite(n) || n === 0) return "";
  const fmt = decimals === 0 ? IN_FMT_INT : IN_FMT;
  return `₹${fmt.format(n)}`;
}

export function MoneyInput({
  value = "",
  onChange,
  min,
  max,
  decimals = 2,
  className = "",
  ...rest
}) {
  const [focused, setFocused] = useState(false);
  const [raw, setRaw] = useState(() => toRaw(value));

  // Keep raw in sync when parent updates value while we're NOT focused
  useEffect(() => {
    if (!focused) setRaw(toRaw(value));
  }, [value, focused]);

  const fire = (numStr) => {
    if (!onChange) return;
    onChange({ target: { value: numStr, name: rest.name || "" } });
  };

  const handleChange = (e) => {
    // Strip currency symbols, commas, spaces — keep digits + one dot + optional minus
    let v = e.target.value.replace(/[₹,\s]/g, "");
    // Only one leading minus allowed if min < 0
    if (min === undefined || min < 0) {
      v = v.replace(/(?!^)-/g, "");
    } else {
      v = v.replace(/-/g, "");
    }
    // Only first "." kept
    const firstDot = v.indexOf(".");
    if (firstDot >= 0) v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, "");
    // Strip non digits (except dot / minus)
    v = v.replace(/[^\d.\-]/g, "");
    setRaw(v);
    // Clamp
    let out = v;
    if (v !== "" && v !== "-" && v !== ".") {
      const n = parseFloat(v);
      if (isFinite(n)) {
        if (min !== undefined && n < min) out = String(min);
        if (max !== undefined && n > max) out = String(max);
      }
    }
    fire(out);
  };

  const displayValue = focused
    ? raw
    : toDisplay(raw !== "" ? raw : value, decimals);

  return (
    <input
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      className={className}
      {...rest}
    />
  );
}

export default MoneyInput;
